import { z } from 'zod';
import { getEnvVar } from '../../utils/env';

const Playist = z.object({
  id: z.string(),
  name: z.string(),
  assetIds: z.array(z.string()),
});

const responseValidator = z.object({
  data: z.array(Playist),
});

export type PlaylistType = z.infer<typeof Playist>;

const ASSET_WATCHLIST_NAME = 'Watchlist-Assets';
const CATEGORY_WATCHLIST_NAME = 'Watchlist-Categories';

export const getPlaylists = ({
  jwt,
  subprofileToken,
}: {
  jwt: string;
  subprofileToken: string;
}) => {
  if (!jwt) {
    return Promise.resolve([]);
  }

  const authentication = jwt && { authorization: `Bearer ${jwt}` };
  const xVimondSubprofile = subprofileToken && {
    'X-Vimond-Subprofile': subprofileToken,
  };

  const headers = {
    'content-type': 'application/json',
    accept: 'application/json',
    ...authentication,
    ...xVimondSubprofile,
  };

  const requestOptions: RequestInit = {
    method: 'GET',
    headers,
    redirect: 'follow',
  };
  return fetch(
    `https://${getEnvVar('NEXT_PUBLIC_TENANT')}.playlist.cf.${getEnvVar('NEXT_PUBLIC_ENVIRONMENT')}.vmnd.tv/api/v1/playlists`,
    requestOptions,
  )
    .then((res) => res.json())
    .then((json) => {
      const response = responseValidator.parse(json);
      return response.data || [];
    });
};

export const createAssetWatchlist = ({
  jwt,
  subprofileToken,
}: {
  jwt: string;
  subprofileToken: string;
}) => {
  return createWatchlist({ jwt, subprofileToken, key: ASSET_WATCHLIST_NAME });
};

export const createCategoryWatchlist = ({
  jwt,
  subprofileToken,
}: {
  jwt: string;
  subprofileToken: string;
}) => {
  return createWatchlist({
    jwt,
    subprofileToken,
    key: CATEGORY_WATCHLIST_NAME,
  });
};

const createWatchlist = ({
  jwt,
  subprofileToken,
  key,
}: {
  jwt: string;
  subprofileToken: string;
  key: string;
}) => {
  const authentication = jwt && { authorization: `Bearer ${jwt}` };
  const xVimondSubprofile = subprofileToken && {
    'X-Vimond-Subprofile': subprofileToken,
  };

  const headers = {
    'content-type': 'application/json',
    accept: 'application/json',
    ...authentication,
    ...xVimondSubprofile,
  };

  const body = {
    name: key,
    assetIds: [],
  };

  const requestOptions: RequestInit = {
    method: 'POST',
    headers,
    redirect: 'follow',
    body: JSON.stringify(body),
  };
  return fetch(
    `https://${getEnvVar('NEXT_PUBLIC_TENANT')}.playlist.cf.${getEnvVar('NEXT_PUBLIC_ENVIRONMENT')}.vmnd.tv/api/v1/playlists`,
    requestOptions,
  )
    .then((res) => res.json())
    .then((json) => {
      const response = responseValidator.parse(json);
      return response.data[0];
    });
};

export const getAssetWatchlist = async ({
  jwt,
  subprofileToken,
}: {
  jwt: string;
  subprofileToken: string;
}) => {
  return (
    (await getWatchlist({ jwt, subprofileToken, key: ASSET_WATCHLIST_NAME })) ??
    (await createAssetWatchlist({ jwt, subprofileToken }))
  );
};

export const getCategoryWatchlist = async ({
  jwt,
  subprofileToken,
}: {
  jwt: string;
  subprofileToken: string;
}) => {
  return (
    (await getWatchlist({
      jwt,
      subprofileToken,
      key: CATEGORY_WATCHLIST_NAME,
    })) ?? (await createCategoryWatchlist({ jwt, subprofileToken }))
  );
};

const getWatchlist = async ({
  jwt,
  subprofileToken,
  key,
}: {
  jwt: string;
  subprofileToken: string;
  key: string;
}) => {
  const playlists = await getPlaylists({ jwt, subprofileToken });
  return playlists.find((playlist) => playlist.name === key);
};

export const addAssetToWatchlist = async ({
  jwt,
  subprofileToken,
  assetId,
}: {
  jwt: string;
  subprofileToken: string;
  assetId: string;
}) => {
  const watchlist = await getAssetWatchlist({ jwt, subprofileToken });
  if (!watchlist) throw new Error('Watchlist not found');
  return addToWatchList({
    jwt,
    subprofileToken,
    key: watchlist.id,
    id: assetId,
  });
};

export const addCategoryToWatchlist = async ({
  jwt,
  subprofileToken,
  categoryId,
}: {
  jwt: string;
  subprofileToken: string;
  categoryId: string;
}) => {
  const watchlist = await getCategoryWatchlist({ jwt, subprofileToken });
  if (!watchlist) throw new Error('Watchlist not found');
  return addToWatchList({
    jwt,
    subprofileToken,
    key: watchlist.id,
    id: categoryId,
  });
};

const addToWatchList = async ({
  jwt,
  subprofileToken,
  key,
  id,
}: {
  jwt: string;
  subprofileToken: string;
  key?: string;
  id?: string;
}) => {
  const authentication = jwt && { authorization: `Bearer ${jwt}` };
  const xVimondSubprofile = subprofileToken && {
    'X-Vimond-Subprofile': subprofileToken,
  };

  const headers = {
    'content-type': 'application/json',
    accept: 'application/json',
    ...authentication,
    ...xVimondSubprofile,
  };

  const requestOptions: RequestInit = {
    method: 'POST',
    headers,
    redirect: 'follow',
  };

  return fetch(
    `https://${getEnvVar('NEXT_PUBLIC_TENANT')}.playlist.cf.${getEnvVar('NEXT_PUBLIC_ENVIRONMENT')}.vmnd.tv/api/v1/playlists/${key}/assets/${id}`,
    requestOptions,
  )
    .then((res) => res.json())
    .then((json) => {
      const response = responseValidator.parse(json);
      return response.data || [];
    });
};

export const removeAssetFromWatchlist = async ({
  jwt,
  subprofileToken,
  assetId,
}: {
  jwt: string;
  subprofileToken: string;
  assetId: string;
}) => {
  const watchlist = await getAssetWatchlist({ jwt, subprofileToken });
  if (!watchlist) throw new Error('Watchlist not found');
  return removeFromWatchList({
    jwt,
    subprofileToken,
    key: watchlist.id,
    id: assetId,
  });
};

export const removeCategoryFromWatchlist = async ({
  jwt,
  subprofileToken,
  categoryId,
}: {
  jwt: string;
  subprofileToken: string;
  categoryId: string;
}) => {
  const watchlist = await getCategoryWatchlist({ jwt, subprofileToken });
  if (!watchlist) throw new Error('Watchlist not found');
  return removeFromWatchList({
    jwt,
    subprofileToken,
    key: watchlist.id,
    id: categoryId,
  });
};

const removeFromWatchList = async ({
  jwt,
  subprofileToken,
  key,
  id,
}: {
  jwt: string;
  subprofileToken: string;
  key?: string;
  id?: string;
}) => {
  const authentication = jwt && { authorization: `Bearer ${jwt}` };
  const xVimondSubprofile = subprofileToken && {
    'X-Vimond-Subprofile': subprofileToken,
  };

  const headers = {
    'content-type': 'application/json',
    accept: 'application/json',
    ...authentication,
    ...xVimondSubprofile,
  };

  const requestOptions: RequestInit = {
    method: 'DELETE',
    headers,
    redirect: 'follow',
  };
  return fetch(
    `https://${getEnvVar('NEXT_PUBLIC_TENANT')}.playlist.cf.${getEnvVar('NEXT_PUBLIC_ENVIRONMENT')}.vmnd.tv/api/v1/playlists/${key}/assets/${id}`,
    requestOptions,
  )
    .then((res) => res.json())
    .then((json) => {
      const response = responseValidator.parse(json);
      return response.data || [];
    });
};
